import React from 'react';
import { Container, Section } from 'react-bulma-components';
import { usePageTitle } from '../../hooks/usePageTitle';
import styles from './RefundPolicy.module.scss';
import SupportEmailLink from '../../components/SupportEmailLink/SupportEmailLink';
import DiscordInviteButton from '../../components/DiscordInviteButton/DiscordInviteButton';

const RefundPolicy: React.FC = () => {
  usePageTitle({
    title: 'Cancellation & Refunds Policy | OnePlay Store',
    description: 'Learn about OnePlay Store cancellation and refund policy, eligibility criteria, timelines, and how to request refunds for your game purchases.'
  });

  return (
    <Section className={styles.refundPolicySection}>
      <Container>
        <div className={styles.content}>
          <h1 className={styles.title}>Cancellation & Refunds Policy</h1>
          
          <div className={styles.description}>
            This Cancellation & Refunds Policy outlines the terms and conditions for cancellations, replacements, and refunds on the OnePlay Store. Our objective is to ensure a clear and transparent experience for all users. Please review this policy carefully before making a purchase.
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>Cancellation Policy</h2>
            <p className={styles.sectionContent}>
              <strong>1.1</strong> Orders cannot be cancelled once placed. All sales are final upon order confirmation.
            </p>
            <p className={styles.sectionContent}>
              <strong>1.2</strong> Once an order is confirmed and payment is processed, the order cannot be cancelled. This policy applies to all digital products including game keys, gift cards, and wallet codes.
            </p>
            <p className={styles.sectionContent}>
              <strong>1.3</strong> If you have concerns about your order, please contact our customer support team for assistance. While we cannot cancel orders, we may be able to help with refund requests if you meet the eligibility criteria outlined in the Refund Policy section below.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>2. Refund Policy</h2>
            <p className={styles.sectionContent}>
              <strong>2.1 Eligibility for Refunds</strong> A refund may be issued under the following conditions:
            </p>
            <ul className={styles.list}>
              {/* <li>The product key has not been viewed or displayed.</li> */}
              <li>The product key is invalid or has been previously used.</li>
              <li>The product key is defective or non-functional due to technical issues on our end.</li>
            </ul>
            <p className={styles.sectionContent}>
              <strong>2.2 Refund Processing Timeline</strong> Refund requests are typically processed within 5-7 business days after approval. The refund will be credited to the original payment method used for the purchase. Processing times may vary depending on your payment provider.
            </p>
            <p className={styles.sectionContent}>
              <strong>2.3 Refund Amount</strong> The full amount paid for the product will be refunded, excluding any processing fees that may be non-refundable as per payment gateway policies.
            </p>
            <p className={styles.sectionContent}>
              <strong>2.4</strong> OnePlay Store functions as a digital key marketplace and is not the original publisher of the keys. As such, we are unable to revoke or deactivate a key once it has been accessed.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>3. Issues with Product Key</h2>
            <p className={styles.sectionContent}>
              <strong>3.1</strong> If you encounter issues with a product key—such as it being invalid or already redeemed—please contact OnePlay Store Customer Support immediately.
            </p>
            <p className={styles.sectionContent}>
              <strong>3.2</strong> Upon investigation and validation of the issue, a refund or replacement will be processed accordingly.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>4. Requesting a Refund or Replacement</h2>
            <p className={styles.sectionContent}>
              <strong>4.1</strong> To initiate a refund or replacement, contact the OnePlay Store Customer Support team.
            </p>
            <p className={styles.sectionContent}>
              <strong>4.2</strong> You must provide the following details:
            </p>
            <ul className={styles.list}>
              <li>Order ID</li>
              <li>Relevant screenshots or error messages</li>
              <li>A brief explanation of the issue</li>
            </ul>
            <p className={styles.sectionContent}>
              <strong>4.3</strong> Our team will assess the request and, if eligible, guide you through the next steps.
            </p>
          </div>

          <div className={styles.section}>
            <h2 className={styles.sectionTitle}>5. Policy Updates</h2>
            <p className={styles.sectionContent}>
              <strong>5.1</strong> OnePlay Store reserves the right to update or modify this Cancellation & Refunds Policy at any time without prior notice.
            </p>
            <p className={styles.sectionContent}>
              <strong>5.2</strong> Any changes will take effect immediately upon being published on the platform.
            </p>
          </div>

          <div className={styles.agreement}>
            By making a purchase on OnePlay Store, you confirm that you have read, understood, and agreed to the terms outlined in this Cancellation & Refunds Policy.
          </div>

          <div className={styles.section}>
            <div className={styles.stepContent}>
              <h3 className={styles.stepTitle}>OnePlay Support</h3>
              <div className={styles.stepDescription}>
                <div style={{ marginBottom: '12px' }}>
                  For any assistance please contact us via email at <SupportEmailLink /> or join our Discord channel:
                </div>
                <DiscordInviteButton />
              </div>
            </div>
          </div>
        </div>
      </Container>
    </Section>
  );
};

export default RefundPolicy; 